/**
 * Candidate analytics (v2 UI) — data from GET /analytics/candidate-snapshot
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { toast } from 'react-toastify';
import type { CandidateAnalytics } from '../../types/candidateAnalytics';
import AppBackButton from '../common/AppBackButton';
import { BusySpinner, PageBusyLoader } from '../ui/PageBusyLoader';
import { useAnalyticsStore } from '../../store/analyticsStore';
import { useSessionStore } from '../../store/sessionStore';
import { resolveScreeningReadinessPercent } from '../../utils/profileCompletionScore';
import { SKIPSCREEN_ANALYTICS_UPDATED_EVENT } from '../../utils/realtimeEvents';
import './AnalyticsV2.css';

type AnalyticsTab = 'overview' | 'prompts' | 'sections' | 'recommendations';

const sectionLabels: Record<keyof CandidateAnalytics['sections'], string> = {
  profile_basics: 'Profile basics',
  discoverability: 'Discoverability',
  portfolio_depth: 'Portfolio depth',
  prompt_quality: 'Prompt quality',
};

function promptQualityBadgeClass(label: string): string {
  if (label === 'Complete') return 'ah-ph-badge ah-ph-badge--complete';
  if (label === 'Partial' || label === 'Strong') {
    return 'ah-ph-badge ah-ph-badge--strong';
  }
  if (label === 'Needs work') return 'ah-ph-badge ah-ph-badge--work';
  return 'ah-ph-badge ah-ph-badge--incomplete';
}

function formatPercent(value: number) {
  return `${Math.round(value)}%`;
}

function formatDurationSeconds(total: number | undefined) {
  if (total == null || total <= 0) return '—';
  const s = Math.floor(total);
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  const r = s % 60;
  if (m < 60) return r === 0 ? `${m} min` : `${m}m ${r}s`;
  const h = Math.floor(m / 60);
  const m2 = m % 60;
  return `${h}h ${m2}m`;
}

function formatAreaLabel(value: string) {
  return value.replace(/_/g, ' ').replace(/\b\w/g, (char) => char.toUpperCase());
}

function ProgressBar({ value, large = false }: { value: number; large?: boolean }) {
  const pct = Math.min(100, Math.max(0, Math.round(value)));
  return (
    <div className={`ah-bar ${large ? 'ah-bar--lg' : ''}`}>
      <div className="ah-bar-fill" style={{ width: `${pct}%` }} />
    </div>
  );
}

function impactClass(impact: 'high' | 'medium' | 'low') {
  if (impact === 'high') return 'ah-impact ah-impact--high';
  if (impact === 'medium') return 'ah-impact ah-impact--medium';
  return 'ah-impact ah-impact--low';
}

export default function AnalyticsV2Page() {
  const [tab, setTab] = useState<AnalyticsTab>('overview');
  const analytics = useAnalyticsStore((state) => state.candidateAnalytics);
  const loading = useAnalyticsStore((state) => state.loadingCandidateAnalytics);
  const error = useAnalyticsStore((state) => state.candidateAnalyticsError);
  const fetchCandidateAnalytics = useAnalyticsStore((state) => state.fetchCandidateAnalytics);
  const currentUser = useSessionStore((state) => state.currentUser);
  const prompts = useSessionStore((state) => state.prompts);
  const portfolio = useSessionStore((state) => state.portfolio);

  const screeningReadinessPercent = useMemo(
    () =>
      resolveScreeningReadinessPercent(
        currentUser as Record<string, unknown> | null,
        prompts,
        portfolio as Record<string, unknown> | null
      ),
    [currentUser, prompts, portfolio]
  );
  const [bootstrapping, setBootstrapping] = useState(
    () => !useAnalyticsStore.getState().candidateAnalytics
  );

  const loadAnalytics = useCallback(
    async (opts?: { force?: boolean }) => {
      try {
        await useSessionStore.getState().refreshUser({ force: Boolean(opts?.force) });
        await fetchCandidateAnalytics({ force: Boolean(opts?.force) });
      } catch (err: unknown) {
        const msg =
          (err as { response?: { data?: { message?: string } } })?.response?.data?.message ||
          'Failed to load analytics';
        toast.error(msg);
      }
    },
    [fetchCandidateAnalytics]
  );

  useEffect(() => {
    let active = true;
    void loadAnalytics({ force: false }).finally(() => {
      if (active) setBootstrapping(false);
    });
    return () => {
      active = false;
    };
  }, [loadAnalytics]);

  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState === 'visible') {
        void loadAnalytics({ force: true });
      }
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, [loadAnalytics]);

  useEffect(() => {
    const onRealtimeEngagement = () => void loadAnalytics({ force: true });
    window.addEventListener(SKIPSCREEN_ANALYTICS_UPDATED_EVENT, onRealtimeEngagement);
    return () =>
      window.removeEventListener(SKIPSCREEN_ANALYTICS_UPDATED_EVENT, onRealtimeEngagement);
  }, [loadAnalytics]);

  const sortedPrompts = useMemo(() => {
    if (!analytics) return [];
    return [...analytics.prompts].sort(
      (left, right) => right.completeness_percent - left.completeness_percent
    );
  }, [analytics]);

  const strongestSection = analytics
    ? (sectionLabels[analytics.insights.strongest_area as keyof CandidateAnalytics['sections']] ??
      formatAreaLabel(analytics.insights.strongest_area))
    : null;
  const weakestSection = analytics
    ? (sectionLabels[analytics.insights.weakest_area as keyof CandidateAnalytics['sections']] ??
      formatAreaLabel(analytics.insights.weakest_area))
    : null;

  const coveragePercent = analytics?.overview.prompt_count
    ? Math.round((analytics.overview.prompts_with_video / analytics.overview.prompt_count) * 100)
    : 0;

  const portfolioCounts = analytics?.overview.portfolio_counts;

  const showInitialLoader = !analytics && (loading || bootstrapping);

  if (showInitialLoader) {
    return (
      <div className="analytics-v2-page">
        <section className="ah-page max-w-6xl pb-8">
          <div className="app-page-back-row">
            <AppBackButton to="/dashboard" />
          </div>
          <PageBusyLoader pageName="Analytics" />
        </section>
      </div>
    );
  }

  const refreshOverlay =
    loading && analytics
      ? createPortal(
          <div
            className="analytics-v2-page__refresh-overlay"
            role="status"
            aria-live="polite"
            aria-label="Refreshing analytics"
          >
            <BusySpinner size="md" />
            <span className="analytics-v2-page__refresh-label">Refreshing analytics…</span>
          </div>,
          document.body
        )
      : null;

  return (
    <div className="analytics-v2-page">
      {refreshOverlay}
      <section className="ah-page max-w-6xl pb-8">
        {error && !analytics ? (
          <article className="rounded-[1.75rem] border border-rose-200 bg-rose-50 p-4 text-sm text-rose-700 shadow-sm">
            {error}
          </article>
        ) : null}

        {analytics ? (
          <>
            <header className="ah-page-header">
              <AppBackButton to="/dashboard" className="ah-page-back" />
              <p className="ah-kicker">Insights</p>
              <div className="mt-1 flex flex-wrap items-center justify-between gap-3">
                <h1 className="font-['Space_Grotesk'] text-2xl font-semibold tracking-tight text-slate-900 md:text-3xl">
                  Analytics
                </h1>
                <button
                  type="button"
                  className="inline-flex items-center gap-1.5 rounded-lg border border-slate-300 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 transition-colors hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-60"
                  disabled={loading}
                  onClick={() => void loadAnalytics({ force: true })}
                >
                  {loading ? (
                    <>
                      <BusySpinner size="sm" />
                      Refreshing…
                    </>
                  ) : (
                    'Refresh analytics'
                  )}
                </button>
              </div>
              <p className="mt-1.5 max-w-2xl text-sm text-slate-600">
                Discoverability, prompt completeness, and recruiter engagement. Snapshot:{' '}
                <span className="font-medium text-slate-800">
                  {new Date(analytics.generated_at).toLocaleString()}
                </span>
                .
              </p>
            </header>

            <article className="ah-hero">
              <div className="ah-hero-score">
                <div
                  className="ah-score-ring"
                  style={{
                    background: `conic-gradient(#059669 ${screeningReadinessPercent * 3.6}deg, #e2e8f0 0deg)`,
                  }}
                >
                  <div className="ah-score-ring-inner">
                    <span className="ah-score-value">{screeningReadinessPercent}</span>
                    <span className="ah-score-cap">/ 100</span>
                  </div>
                </div>
                <p className="mt-2 text-center text-[11px] font-medium text-slate-500">
                  Screening readiness
                </p>
                <span
                  className={`ah-pill mt-2 ${analytics.overview.public_profile_active ? 'ah-pill--ok' : ''}`}
                >
                  {analytics.overview.public_profile_active
                    ? 'Public profile live'
                    : 'Public profile off'}
                </span>
              </div>
              <div className="ah-hero-body">
                <p className="ah-hero-summary">
                  <strong>{strongestSection}</strong> is your strongest area. Focus on{' '}
                  <strong>{weakestSection}</strong> for the biggest lift. Prompts average{' '}
                  <strong>{formatPercent(analytics.insights.average_prompt_completeness)}</strong>{' '}
                  completeness.
                </p>
                <div className="ah-stat-grid ah-stat-grid--4 mt-3">
                  {[
                    {
                      label: 'Prompts',
                      value: analytics.overview.prompt_count,
                      sub: 'in library',
                    },
                    {
                      label: 'Recruiter reach',
                      value: analytics.engagement.unique_recruiter_views,
                      sub: 'unique viewers',
                    },
                    {
                      label: 'Likes',
                      value: analytics.engagement.section_likes,
                      sub: 'on sections',
                    },
                    {
                      label: 'Video coverage',
                      value: `${coveragePercent}%`,
                      sub: 'of prompts',
                    },
                  ].map((cell) => (
                    <div key={cell.label} className="ah-stat-cell">
                      <div className="ah-stat-label">{cell.label}</div>
                      <div className="ah-stat-value">{cell.value}</div>
                      <div className="ah-stat-sub">{cell.sub}</div>
                    </div>
                  ))}
                </div>
              </div>
            </article>

            <article className="ah-panel">
              <div className="flex flex-wrap items-end justify-between gap-2">
                <div>
                  <p className="ah-kicker">Activity</p>
                  <h2 className="ah-panel-title">Engagement & requests</h2>
                </div>
                <p className="ah-panel-note">All-time from your profile</p>
              </div>
              <div className="ah-metric-grid mt-3">
                {[
                  ['Profile views (total)', analytics.engagement.profile_views],
                  ['Unique profile views', analytics.engagement.unique_recruiter_views],
                  ['Prompt opens (total)', analytics.engagement.prompt_views],
                  ['Unique prompt opens', analytics.engagement.unique_prompt_views ?? 0],
                  ['Portfolio section expands', analytics.engagement.section_expands ?? 0],
                  ['Unique section expanders', analytics.engagement.unique_section_expands ?? 0],
                  ['Section likes (total)', analytics.engagement.section_likes],
                  ['Unique recruiters (likes)', analytics.engagement.unique_section_likes ?? 0],
                  ['Document opens', analytics.engagement.document_views],
                  [
                    'Recruiter time on profile',
                    formatDurationSeconds(analytics.engagement.profile_dwell_seconds_total),
                  ],
                  [
                    'Prompt video watched',
                    formatDurationSeconds(analytics.engagement.prompt_video_watch_seconds_total),
                  ],
                  ['Complete prompts', analytics.overview.complete_prompts],
                  ['Documents attached', analytics.overview.total_documents],
                  ['Question requests', analytics.engagement.question_requests],
                  ['Chat requests', analytics.engagement.chat_requests_total ?? 0],
                  ['Access approvals', analytics.engagement.approved_access_requests],
                  ['Recruiters who saved you', analytics.engagement.recruiter_saves_total ?? 0],
                ].map(([label, value]) => (
                  <div key={String(label)} className="ah-metric-row">
                    <span className="ah-metric-label">{label}</span>
                    <span className="ah-metric-value">
                      {typeof value === 'number' || typeof value === 'string' ? String(value) : '—'}
                    </span>
                  </div>
                ))}
              </div>
            </article>

            {portfolioCounts ? (
              <article className="ah-panel">
                <div className="flex flex-wrap items-end justify-between gap-2">
                  <div>
                    <p className="ah-kicker">Portfolio</p>
                    <h2 className="ah-panel-title">Section inventory</h2>
                  </div>
                  <p className="ah-panel-note">Updates when you edit portfolio</p>
                </div>
                <div className="ah-inv-grid mt-3">
                  {(
                    [
                      ['Projects', portfolioCounts.projects],
                      ['Certifications', portfolioCounts.certifications],
                      ['Organizations', portfolioCounts.organizations],
                      ['Accomplishments', portfolioCounts.accomplishments],
                      ['References', portfolioCounts.references],
                    ] as const
                  ).map(([label, n]) => (
                    <div key={label} className="ah-inv-cell">
                      <div className="ah-inv-value">{n}</div>
                      <div className="ah-inv-label">{label}</div>
                    </div>
                  ))}
                </div>
              </article>
            ) : null}

            {(analytics.engagement.chat_requests_total ?? 0) > 0 ||
            (analytics.engagement.chat_requests_pending ?? 0) > 0 ? (
              <article className="ah-panel">
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div>
                    <p className="ah-kicker">Messages</p>
                    <h2 className="ah-panel-title">Recruiter chat requests</h2>
                  </div>
                  <div className="flex flex-wrap gap-4 text-sm">
                    <span className="text-slate-500">
                      Pending{' '}
                      <strong className="text-slate-800">
                        {analytics.engagement.chat_requests_pending ?? 0}
                      </strong>
                    </span>
                    <span className="text-slate-500">
                      Accepted{' '}
                      <strong className="text-slate-800">
                        {analytics.engagement.chat_requests_accepted ?? 0}
                      </strong>
                    </span>
                    <span className="text-slate-500">
                      Total{' '}
                      <strong className="text-slate-800">
                        {analytics.engagement.chat_requests_total ?? 0}
                      </strong>
                    </span>
                  </div>
                </div>
              </article>
            ) : null}

            <nav className="ah-tabs" aria-label="Analytics sections">
              {[
                { key: 'overview', label: 'Overview' },
                { key: 'prompts', label: 'Prompt health' },
                { key: 'sections', label: 'Section scores' },
                { key: 'recommendations', label: 'Recommendations' },
              ].map((item) => (
                <button
                  key={item.key}
                  type="button"
                  className={`ah-tab ${tab === item.key ? 'ah-tab--active' : ''}`}
                  onClick={() => setTab(item.key as AnalyticsTab)}
                >
                  {item.label}
                </button>
              ))}
            </nav>

            {tab === 'overview' ? (
              <div className="ah-tab-grid ah-tab-grid--split">
                <article className="ah-panel">
                  <div className="flex flex-wrap items-start justify-between gap-2">
                    <div>
                      <p className="ah-kicker">Recruiter readiness</p>
                      <h2 className="ah-panel-title">What recruiters can meaningfully review</h2>
                    </div>
                    <span className="ah-pill ah-pill--ok">
                      {formatPercent(analytics.insights.average_prompt_completeness)} avg
                      completeness
                    </span>
                  </div>

                  <div className="ah-coverage-grid mt-3">
                    <div className="ah-coverage-cell">
                      <div className="ah-coverage-label">With video</div>
                      <div className="ah-coverage-value">
                        {analytics.overview.prompts_with_video}
                      </div>
                    </div>
                    <div className="ah-coverage-cell">
                      <div className="ah-coverage-label">With documents</div>
                      <div className="ah-coverage-value">
                        {analytics.overview.prompts_with_documents}
                      </div>
                    </div>
                    <div className="ah-coverage-cell">
                      <div className="ah-coverage-label">With description</div>
                      <div className="ah-coverage-value">
                        {analytics.overview.prompts_with_description}
                      </div>
                    </div>
                  </div>

                  <div className="ah-mini-list">
                    {sortedPrompts.slice(0, 4).map((prompt) => {
                      const pct = Math.round(prompt.completeness_percent);
                      return (
                        <div key={prompt.id} className="ah-ph-card">
                          <div className="ah-ph-top">
                            <div className="min-w-0">
                              <div className="ah-ph-title line-clamp-2">{prompt.title}</div>
                              <div className="ah-ph-meta">
                                Updated {new Date(prompt.updated_at).toLocaleDateString()}
                              </div>
                            </div>
                            <span className={promptQualityBadgeClass(prompt.quality_label)}>
                              {prompt.quality_label}
                            </span>
                          </div>
                          <div className="ah-ph-bar ah-ph-bar--full">
                            <div className="ah-ph-bar-fill" style={{ width: `${pct}%` }} />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </article>

                <article className="ah-panel">
                  <p className="ah-kicker">Key signals</p>
                  <div className="ah-signal-list">
                    <div className="ah-signal-item">
                      <div className="ah-signal-label">Strongest</div>
                      <div className="ah-signal-value">{strongestSection}</div>
                    </div>
                    <div className="ah-signal-item">
                      <div className="ah-signal-label">Needs attention</div>
                      <div className="ah-signal-value">{weakestSection}</div>
                    </div>
                    <div className="ah-signal-item">
                      <div className="ah-signal-label">Recruiter engagement</div>
                      <div className="ah-signal-value">
                        {analytics.engagement.profile_views} profile views (
                        {analytics.engagement.unique_recruiter_views} unique) ·{' '}
                        {analytics.engagement.section_expands ?? 0} portfolio expands ·{' '}
                        {analytics.engagement.section_likes} likes ·{' '}
                        {analytics.engagement.question_requests} question requests
                      </div>
                      <div className="ah-signal-detail">
                        Access requests: {analytics.engagement.access_requests}. Approved:{' '}
                        {analytics.engagement.approved_access_requests}. Chat requests:{' '}
                        {analytics.engagement.chat_requests_total ?? 0} (pending{' '}
                        {analytics.engagement.chat_requests_pending ?? 0}).
                      </div>
                    </div>
                  </div>
                </article>
              </div>
            ) : null}

            {tab === 'prompts' ? (
              <article className="ah-panel">
                <div className="flex flex-col gap-1 sm:flex-row sm:items-end sm:justify-between">
                  <div>
                    <p className="ah-kicker">Prompt health</p>
                    <h2 className="ah-panel-title">Completeness by prompt</h2>
                  </div>
                  <p className="ah-panel-note max-w-md">
                    Video, document, and description required for 100%.
                  </p>
                </div>
                <div className="ah-ph-list mt-4">
                  {sortedPrompts.map((prompt) => {
                    const pct = Math.round(prompt.completeness_percent);
                    return (
                      <div key={prompt.id} className="ah-ph-card">
                        <div className="ah-ph-top">
                          <div className="min-w-0">
                            <div className="ah-ph-title line-clamp-2">{prompt.title}</div>
                            <div className="ah-ph-meta">
                              {prompt.documents_count} doc
                              {prompt.documents_count === 1 ? '' : 's'}
                              {' · '}
                              {prompt.status}
                              {prompt.has_video && (prompt.video_watch_seconds_total ?? 0) > 0
                                ? ` · ${formatDurationSeconds(prompt.video_watch_seconds_total)} watched`
                                : null}
                            </div>
                          </div>
                          <div className="ah-ph-score-col">
                            <span className="ah-ph-pct">
                              {formatPercent(prompt.completeness_percent)}
                            </span>
                            <span className={promptQualityBadgeClass(prompt.quality_label)}>
                              {prompt.quality_label}
                            </span>
                          </div>
                        </div>
                        <div className="ah-ph-bottom">
                          <div className="ah-ph-checks">
                            <span
                              className={`ah-ph-check ${prompt.has_video ? 'ah-ph-check--ok' : ''}`}
                            >
                              Video
                            </span>
                            <span
                              className={`ah-ph-check ${prompt.has_documents ? 'ah-ph-check--ok' : ''}`}
                            >
                              Documents
                            </span>
                            <span
                              className={`ah-ph-check ${prompt.has_description ? 'ah-ph-check--ok' : ''}`}
                            >
                              Description
                            </span>
                          </div>
                          <div className="ah-ph-bar" title={`${pct}% complete`}>
                            <div className="ah-ph-bar-fill" style={{ width: `${pct}%` }} />
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </article>
            ) : null}

            {tab === 'sections' ? (
              <div className="ah-tab-grid ah-tab-grid--split-reverse">
                <article className="ah-panel">
                  <p className="ah-kicker">Screening readiness</p>
                  <div className="ah-composite-value">{screeningReadinessPercent}</div>
                  <ProgressBar value={screeningReadinessPercent} large />
                  <p className="ah-panel-note mt-3">
                    Matches your dashboard score (9 weighted profile sections). The section
                    breakdown on the right uses recruiter analytics buckets and may differ.
                  </p>
                </article>

                <article className="ah-panel">
                  <p className="ah-kicker">Section scores</p>
                  <div className="ah-section-list">
                    {Object.entries(analytics.sections).map(([key, value]) => (
                      <div key={key}>
                        <div className="ah-section-row-head">
                          <span className="ah-section-name">
                            {sectionLabels[key as keyof CandidateAnalytics['sections']]}
                          </span>
                          <span className="ah-section-pct">{formatPercent(value)}</span>
                        </div>
                        <ProgressBar value={value} />
                      </div>
                    ))}
                  </div>
                </article>
              </div>
            ) : null}

            {tab === 'recommendations' ? (
              <div className="ah-tab-grid ah-tab-grid--rec">
                <article className="ah-panel">
                  <p className="ah-kicker">Recommended next moves</p>
                  <div className="ah-rec-list">
                    {analytics.recommendations.length ? (
                      analytics.recommendations.map((item) => (
                        <div key={item.key} className="ah-rec-item">
                          <div className="ah-rec-top">
                            <div className="ah-rec-title">{item.title}</div>
                            <span className={impactClass(item.impact)}>{item.impact} impact</span>
                          </div>
                          <p className="ah-rec-detail">{item.detail}</p>
                        </div>
                      ))
                    ) : (
                      <div className="ah-empty-ok">
                        No immediate gaps detected. Your profile already covers the major recruiter
                        signals.
                      </div>
                    )}
                  </div>
                </article>

                <article className="ah-panel">
                  <p className="ah-kicker">What this means</p>
                  <div className="ah-signal-list">
                    <div className="ah-signal-item">
                      <div className="ah-signal-label">Best-performing area</div>
                      <div className="ah-signal-value">{strongestSection}</div>
                    </div>
                    <div className="ah-signal-item">
                      <div className="ah-signal-label">Highest leverage fix</div>
                      <div className="ah-signal-value">{weakestSection}</div>
                    </div>
                    <div className="ah-signal-item">
                      <div className="ah-signal-label">Snapshot generated</div>
                      <div className="ah-signal-value">
                        {new Date(analytics.generated_at).toLocaleString()}
                      </div>
                    </div>
                  </div>
                </article>
              </div>
            ) : null}
          </>
        ) : null}
      </section>
    </div>
  );
}
